import React, {useEffect, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export const useIsOffline = (): boolean => {
  const [isOffline, setIsOffline] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return !navigator.onLine;
  });

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOffline;
};

type OfflineBannerProps = {
  message: string;
  heightPx?: number;
  zIndex?: number;
};

const OfflineBanner = ({message, heightPx = 18, zIndex = 9999}: OfflineBannerProps) => {
  const isOffline = useIsOffline();

  const styles = useMemo(() => {
    const bg = 'rgba(43, 107, 253, 0.10)'; // light Libretto blue
    const border = 'rgba(43, 107, 253, 0.18)';
    return {
      container: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: `${heightPx}px`,
        backgroundColor: bg,
        borderBottom: `1px solid ${border}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex,
        overflow: 'hidden',
        paddingLeft: '12px',
        paddingRight: '12px',
        pointerEvents: 'none',
      },
      text: {
        color: '#1e2022',
        fontFamily: 'Inter, sans-serif',
        fontSize: '13px',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      },
    } as const;
  }, [heightPx, zIndex]);

  if (!isOffline) return null;

  return (
    <Box sx={styles.container} role="status" aria-live="polite" aria-label={`You are offline. ${message}`}>
      <Typography sx={styles.text}>
        You are offline. {message}
      </Typography>
    </Box>
  );
};

export default OfflineBanner;
